import styles from "./runTestCasesButton.module.scss";
import { useTranslation } from "react-i18next";
import {
  TestCaseTemplate,
  TransformTestCase
} from "../../types/testCaseTemplateTypes";
import {
  ResultEnum,
  TransformExecuteResponse
} from "../../types/clientTypes";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../store";
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useExecuteRequestMutation } from "../../api/clientService";
import { useRunTestrailTestCaseGroupMutation } from "../../api/testrailService";
import {
  runTestCases,
  stopTestCases
} from "../../store/slices/mainSlice";
import {
  getRequestHeaders,
  getServer
} from "../../utils/testCaseUtils";
import {
  getExecuteResult,
  getTestrailComment,
  getTestrailStatusId
} from "../../utils/executeResultUtils";
import { ErrorType } from "../../types/sharedTypes";
import { TestrailTestCaseRunRequest } from "../../types/testrailTypes";
import { findProjectSettings } from "../../store/selectors";

const countTestCaseGroup = +(process.env.REACT_APP_COUNT_TEST_CASE_GROUP as string);

type RunStopButtonProps = {
  selectedTestCases: Array<TransformTestCase>,
  updateExecuteResponse: (executeResponse: TransformExecuteResponse) => void,
  testCaseTemplates: Array<TestCaseTemplate>,
  clickRun?: () => void;
  beforeRunIfWasLaunched?: () => void;
  clickStop?: () => void;
  disabledRunButton?: boolean;
  stopEvent?: () => void;
  hideStopButton?: boolean;
}

export const RunTestCasesButton = ({ selectedTestCases, testCaseTemplates, beforeRunIfWasLaunched, updateExecuteResponse, clickRun, clickStop, disabledRunButton, stopEvent, hideStopButton }: RunStopButtonProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const methods = useSelector((state: RootState) => state.mainSlice.methods);
  const testrailEnable = useSelector((state: RootState) => findProjectSettings(state).testrailEnable);
  const statusCodes = useSelector((state: RootState) => state.mainSlice.statusCodes);
  const selectedEnvironment = useSelector((state: RootState) => findProjectSettings(state).selectedEnvironment);
  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const [wasLaunched, setWasLaunched] = useState<boolean>(false);
  const [indexRunTestCase, setIndexRunTestCase] = useState<number>();
  const [needRunTestRailExecuteResponse, setNeedRunTestRailExecuteResponse] = useState<Array<TransformExecuteResponse>>([]);

  const [sendExecuteRequest, { data: sendRequestData, error: sendRequestError }] = useExecuteRequestMutation();
  const [runTestrailTestCaseGroup] = useRunTestrailTestCaseGroupMutation();

  const stop = useCallback(() => {
    setIndexRunTestCase(undefined);
  }, [setIndexRunTestCase]);

  useEffect(() => {
    if (indexRunTestCase == undefined && wasLaunched) {
      dispatch(stopTestCases());
      !!stopEvent && stopEvent();
    }
  }, [indexRunTestCase, wasLaunched, stopEvent]);

  useEffect(() => {
    runTestCase();
  }, [indexRunTestCase]);

  useEffect(() => {
    if (!!sendRequestData || !!sendRequestError) {
      processingResponse();
    }
  }, [sendRequestData, sendRequestError]);

  useEffect(() => {
    if (indexRunTestCase != null && needRunTestRailExecuteResponse.length === countTestCaseGroup) {
      runTestRail();
    }
    if (!!needRunTestRailExecuteResponse.length && indexRunTestCase == null) {
      runTestRail();
    }
  }, [needRunTestRailExecuteResponse, indexRunTestCase]);

  useEffect(() => {
    stop();
  }, [selectedVariables, stop]);

  const runTestCase = () => {
    if (indexRunTestCase != null && !!selectedTestCases[indexRunTestCase]) {
      const testCase = selectedTestCases[indexRunTestCase]
      const testCaseTemplate = testCaseTemplates?.find(item => item.id === testCase.testCaseTemplateId);
      const findMethod = methods.find(method => method.id === testCaseTemplate?.methodId);
      if (findMethod && testCaseTemplate) {
        const server = getServer(testCaseTemplate, selectedEnvironment);
        if (!!server?.id) {
          updateExecuteResponse({
            statusCode: 0,
            responseBody: "",
            time: 0,
            isLoad: true,
            internalId: testCase.internalId || testCase.id?.toString()
          });
          sendExecuteRequest({
            method: findMethod.name,
            serverId: server.id,
            requestHeader: getRequestHeaders(selectedVariables, testCase, selectedEnvironment.defaultHeaders),
            endpoint: testCase.endpoint,
            parameters: testCase.payload,
            body: testCase.body,
            responseJsonSchemaForValidation: testCaseTemplate.isJsonSchemaValidation ? testCase.expectedBody : undefined,
            internalId: testCase.internalId || testCase.id?.toString()
          });
        } else {
          updateExecuteResponse({
            statusCode: -1,
            responseBody: "",
            time: 0,
            internalId: testCase.internalId || testCase.id?.toString(),
            result: ResultEnum.skipped
          })
          setIndexRunTestCase(indexRunTestCase + 1);
        }
      }
    } else if (indexRunTestCase != null && !selectedTestCases[indexRunTestCase]) {
      setIndexRunTestCase(undefined);
    }
  }

  const processingResponse = () => {
    if (!!sendRequestData) {
      const newData: TransformExecuteResponse = { ...sendRequestData };
      const testCase = selectedTestCases.find(item => (item.internalId || item.id?.toString()) === newData.internalId);
      const testCaseTemplate = testCaseTemplates?.find(item => item.id === testCase?.testCaseTemplateId);
      if (!!testCaseTemplate && !!testCase) {
        newData.result = getExecuteResult(selectedVariables, newData, testCaseTemplate, testCase, statusCodes);
      }
      if (!!testCase?.testRailTestCaseId && testrailEnable && !!testCaseTemplate && !!selectedEnvironment.testRunId) {
        setNeedRunTestRailExecuteResponse([...needRunTestRailExecuteResponse, newData]);
      }
      updateExecuteResponse(newData);
    }
    if (!!sendRequestError) {
      const error = sendRequestError as ErrorType;
      updateExecuteResponse({
        isError: true,
        statusCode: error.status || 0,
        result: ResultEnum.serverError,
        time: 0,
        responseBody: "",
        internalId: error.internalId
      });
    }
    if (indexRunTestCase != null) {
      setIndexRunTestCase(indexRunTestCase + 1);
    }
  }

  const runTestRail = () => {
    const testrailTestCaseRunRequest: Array<TestrailTestCaseRunRequest> = [];
    const newNeedRunTestRailExecuteResponse = [...needRunTestRailExecuteResponse];
    const executeResponses = newNeedRunTestRailExecuteResponse.splice(0, countTestCaseGroup);
    setNeedRunTestRailExecuteResponse(newNeedRunTestRailExecuteResponse);
    executeResponses.forEach((executeResponse) => {
      const testCase = selectedTestCases.find(item => (item.internalId || item.id?.toString()) === executeResponse.internalId);
      const testCaseTemplate = testCaseTemplates?.find(item => item.id === testCase?.testCaseTemplateId);
      if (testCaseTemplate && testCase) {
        const statusId = getTestrailStatusId(executeResponse);
        updateExecuteResponse({
          ...executeResponse,
          isLoadTestrail: true
        })
        testrailTestCaseRunRequest.push({
          runId: selectedEnvironment.testRunId as number,
          testCaseId: testCase.testRailTestCaseId as number,
          statusId,
          comment: getTestrailComment(statusId, executeResponse)
        });
      }
    });
    if (testrailTestCaseRunRequest.length) {
      runTestrailTestCaseGroup(testrailTestCaseRunRequest).then(testrailResult => {
        executeResponses.forEach(executeResponse => {
          let testRailResultEnum: ResultEnum;
          if (!!testrailResult.error) {
            testRailResultEnum = ResultEnum.failed;
          } else {
            testRailResultEnum = ResultEnum.passed;
          }
          updateExecuteResponse({
            ...executeResponse,
            isLoadTestrail: false,
            testrailResult: testRailResultEnum
          })
        });
      })
    }
  }

  const start = () => {
    setWasLaunched(true);
    setIndexRunTestCase(0);
    dispatch(runTestCases());
  }

  const isRunning = useMemo(() => {
    return indexRunTestCase !== undefined;
  }, [indexRunTestCase]);

  const localClickRun = (): void => {
    if (wasLaunched) {
      beforeRunIfWasLaunched && beforeRunIfWasLaunched();
    }
    !!clickRun && clickRun();
    start();
  }

  const localClickStop = (): void => {
    clickStop && clickStop();
    stop();
  }

  return (<>
    {!isRunning ? (
    <button className={styles.runButton}
            disabled={disabledRunButton}
            onClick={localClickRun}>
      {t("common.run")}
    </button>
    ) : !hideStopButton && (
    <button className={styles.cancelButton}
            onClick={localClickStop}>
      {t("common.stop")}
    </button>
    )}
  </>);
}